import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessagesSquare, ArrowRight, Lock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Container } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";

export default function Forum() {
  const { user, ready } = useAuth();
  const [boards, setBoards] = useState([]);
  const [allowance, setAllowance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/forum/boards").then((r) => setBoards(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) api.get("/forum/allowance").then((r) => setAllowance(r.data)).catch(() => {});
  }, [user]);

  if (!ready) return <div className="py-40 flex justify-center"><Loader2 className="animate-spin text-[#CDA434]" size={32} /></div>;

  return (
    <div>
      <PageHero
        kicker="Members' forum · By sector"
        title="The trade rooms"
        intro="A members' forum split by industry, for firms trading across the two Bostons. Introduce your business, post capacity, and find partners on the other shore."
        dark
      />

      <section className="py-12 sm:py-16">
        <Container>
          {!user && (
            <div data-testid="forum-guest-notice" className="mb-8 bg-[#003366]/5 border border-[#003366]/20 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="font-sans text-sm text-[#1B2430]">
                You're browsing the forum as a guest during our review phase. <Link to="/signin" className="font-semibold text-[#003366] link-underline">Sign in</Link> or <Link to="/signup" className="font-semibold text-[#003366] link-underline">create an account</Link> to start a thread or reply.
              </p>
            </div>
          )}
          {allowance && !allowance.unlimited && (
            <div data-testid="forum-allowance" className="mb-8 bg-[#CDA434]/10 border border-[#CDA434]/50 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="font-sans text-sm text-[#1B2430]">
                <Lock size={14} className="inline mr-1.5 -mt-0.5 text-[#7a611c]" />
                Free members may make <strong>one</strong> forum contribution ({allowance.used}/{allowance.cap} used). Upgrade to post without limit.
              </p>
              <Link to="/join" className="font-sans text-sm font-semibold text-[#003366] whitespace-nowrap link-underline">See plans →</Link>
            </div>
          )}

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-[#FFFAF3] border border-[#D6D3CB] animate-pulse" />)}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {boards.map((b) => (
                <Link key={b.sector} to={`/forum/board/${encodeURIComponent(b.sector)}`} data-testid={`forum-board-${b.sector}`}
                  className="group bg-[#FFFAF3] border border-[#D6D3CB] hover:border-[#CDA434] transition-colors p-6 flex flex-col">
                  <div className="w-11 h-11 border border-[#CDA434] flex items-center justify-center text-[#CDA434] mb-4">
                    <MessagesSquare size={20} />
                  </div>
                  <h3 className="font-serif text-lg text-[#1E1E2F] group-hover:text-[#003366] transition-colors">{b.sector}</h3>
                  <p className="font-sans text-sm text-[#505A68] mt-2 flex-1">
                    {b.thread_count === 0 ? "No threads yet — start the first." : b.latest}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1 font-sans text-xs uppercase tracking-wide text-[#003366] font-semibold">
                    {b.thread_count} thread{b.thread_count === 1 ? "" : "s"} <ArrowRight size={13} />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </Container>
      </section>
    </div>
  );
}
